/**
 * Centralized animation configurations for Framer Motion
 * Use these consistent animations throughout the application
 */

import { Transition, Variants } from "framer-motion";

// ============================================
// TRANSITION PRESETS
// ============================================

export const transitions = {
  quick: { duration: 0.15, ease: "easeOut" } as Transition,
  normal: { duration: 0.25, ease: "easeOut" } as Transition,
  smooth: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } as Transition,
  spring: { type: "spring" as const, stiffness: 400, damping: 30 } as Transition,
  springGentle: { type: "spring" as const, stiffness: 300, damping: 25 } as Transition,
};

// ============================================
// BASIC ANIMATIONS
// ============================================

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: transitions.quick,
};

export const fadeInFast = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.1 },
};

// ============================================
// PAGE TRANSITIONS
// ============================================

export const slideUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: transitions.normal,
};

export const slideDown = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 10 },
  transition: transitions.normal,
};

export const slideLeft = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: transitions.normal,
};

export const slideRight = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
  transition: transitions.normal,
};

// ============================================
// MODAL/DIALOG ANIMATIONS
// ============================================

export const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
  transition: transitions.normal,
};

export const modalOverlay = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25, ease: "easeOut" },
};

export const modalContent = {
  initial: { opacity: 0, scale: 0.96, y: 8 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96, y: 8 },
  transition: { duration: 0.2, ease: "easeOut" },
};

// ============================================
// SHEET/DRAWER ANIMATIONS
// ============================================

export const sheetFromRight = {
  initial: { x: "100%", opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: "100%", opacity: 0 },
  transition: { 
    duration: 0.35, 
    ease: [0.32, 0.72, 0, 1],
    opacity: { duration: 0.25 }
  },
};

export const sheetFromLeft = {
  initial: { x: "-100%" },
  animate: { x: 0 },
  exit: { x: "-100%" },
  transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
};

export const sheetFromBottom = {
  initial: { y: "100%" },
  animate: { y: 0 },
  exit: { y: "100%" },
  transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
};

export const sheetFromTop = {
  initial: { y: "-100%" },
  animate: { y: 0 },
  exit: { y: "-100%" },
  transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
};

// ============================================
// LIST ANIMATIONS (Stagger)
// ============================================

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.03,
      staggerDirection: -1,
    },
  },
};

export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -5 },
};

export const staggerContainerFast: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
};

// ============================================
// HOVER/TAP ANIMATIONS
// ============================================

export const hoverScale = {
  scale: 1.02,
  transition: transitions.quick,
};

export const hoverScaleLarge = {
  scale: 1.05,
  transition: transitions.spring,
};

export const tapScale = {
  scale: 0.98,
  transition: transitions.quick,
};

export const tapScaleSmall = {
  scale: 0.95,
  transition: transitions.quick,
};

// ============================================
// COLLAPSE/EXPAND ANIMATIONS
// ============================================

export const collapseVertical: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: transitions.normal,
  },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: transitions.normal,
  },
};

export const collapseHorizontal: Variants = {
  collapsed: {
    width: 0,
    opacity: 0,
    transition: transitions.normal,
  },
  expanded: {
    width: "auto",
    opacity: 1,
    transition: transitions.normal,
  },
};

// ============================================
// NAVIGATION ANIMATIONS
// ============================================

export const navItem = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -10 },
  transition: transitions.quick,
};

export const navItemHover = {
  x: 4,
  transition: transitions.quick,
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Create a stagger animation with custom delay
 */
export const createStagger = (delay: number = 0.05): Variants => ({
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: delay,
    },
  },
});

/**
 * Create a simple fade with custom duration
 */
export const createFade = (duration: number = 0.2) => ({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration },
});

/**
 * Delay an animation
 */
export const withDelay = (animation: any, delay: number) => ({
  ...animation,
  transition: {
    ...animation.transition,
    delay,
  },
});

